import groupBy from "lodash-es/groupBy";
import type {
  Component as VueComponent,
  CreateElement,
  VNode,
  VueConstructor,
} from "vue";
import DataCellText from "@/components/controls/grid/renderers/DataCellText.vue";
import { autoComparer } from "@/components/controls/grid/BmGridComparers";
import { stringMatcher } from "@/components/controls/grid/BmGridMatchers";
import { allTheSameAggregator } from "@/components/controls/grid/BmGridAggregators";
import type BaseEnumType from "@/app/services/baseEnumType";
import type { BmGridState } from "@/components/controls/grid/state/BmGridState";
import BmGridStateRepository from "@/components/controls/grid/state/BmGridStateRepository";
import type { FilterFunc } from "@/components/controls/grid/columnfilters/ColumnFilters";
import { cloneDeep, isEqual } from "lodash-es";
import eventLogger from "@/app/eventlogging/eventLogger";
import { toMap } from "@/app/apiutils";
import type { RawLocation } from "vue-router";
import type { Maybe } from "@/components/utils/typeHelpers";
import type { BmIconName } from "@/components/controls/bmIconMap";
import type { TextTruncationOptions } from "./builder/ColumnBuilders";

export * from "@/components/controls/grid/state/BmGridState";

export type Comparer = (itemValueA: any, itemValueB: any) => 1 | 0 | -1;
export type Matcher = (itemValue: any, search: string) => boolean;
export type ColumnAlignment = "start" | "center" | "end";
export type Renderer = (
  h: CreateElement,
  value: any,
  item: any,
  context: Renderable
) => VNode;
export type GridRendererComponent = VueConstructor | VueComponent;
export type ColumnFooterRenderer = (h: CreateElement) => VNode;
export type GroupSummaryValueExtractor = (
  itemValues: any[],
  groupRowValue?: any,
  parentGroupValues?: string[]
) => any;
export type ColumnFilter = {
  /**
   * Component to render the controls for the filter. Will set properties 'gridState' and 'columnKey'.
   */
  columnFilterComponent: VueConstructor | VueComponent;

  /**
   * Create a filter function from a given filterState
   */
  buildApply: (filterState: any) => FilterFunc;
};

/**
 * Function to transform a vue component with properties matching the renderer function arguments.
 * @param component A vue component.
 */
export function componentToRenderer(
  component: GridRendererComponent
): Renderer {
  return (
    h: CreateElement,
    value: any,
    item: any,
    context: Renderable
  ): VNode =>
    h(
      component,
      {
        attrs: { value: value, item: item, context: context },
      },
      []
    );
}

export function getGroupRowKey(
  columnId: string,
  groupRowValue: string,
  parentGroupValues: string[] = []
): string {
  // If any are not strings throw an error. Parent group values should already have been checked.
  if (typeof groupRowValue !== "string" || typeof columnId !== "string") {
    eventLogger.logEvent("group-row-key-invalid", {
      columnId,
      groupRowValue,
    });
  }

  return [columnId, ...parentGroupValues, groupRowValue].join("-");
}

export interface SortableAndSearchable {
  comparer: Comparer;
  matcher: Matcher;
  columnFilter?: ColumnFilter;
}

// Formatter for changing the extracted value. For more complex cases you may need to change the renderer.
export interface Formattable {
  formatter: (value: any, item: any) => any;
  csvformatter: (
    value: any,
    item: any,
    stringFormatter: (s: string) => string
  ) => string;
}

export interface RowAction {
  translation: (item: any) => string;
  onClick?: (item: any) => void;
  route?: ((item: any) => RawLocation | undefined) | RawLocation;
  isActive: (item: any) => boolean;
}

export interface RendererMetadata {
  actions?: (RowAction | ((item: any) => RowAction[]))[];
  isAgTruncated?: any;
  textTruncationOptions?: TextTruncationOptions;
  suffixExtractor?: any;
  prefixExtractor?: any;
  iconSuffixExtractor?: (item: any, value: any) => Maybe<BmIconName>;
  locationExtractor?: (item: any, value: any) => Maybe<RawLocation>;
  newTab?: any;
  externalLinkExtractor?: (item: any, value: any) => Maybe<string>;
  emailExtractor?: any;
  phoneExtractor?: any;
  editRenderer?: any;
  control?: any;
}

export interface Renderable {
  cardColumnSpan: number;
  align: ColumnAlignment;
  renderer: Renderer;
  rendererMetadata: RendererMetadata;
}

export const migrateGridState = (
  gridState: BmGridState,
  config: BmGridConfiguration
) => {
  const newColumnOrder = gridState.columnOrder;
  let nextColumnInsertIndex = 0;

  const defaultRemovedColumns = new Set(
    config.defaultGridState?.removedColumnKeys ?? []
  );

  for (const [columnKey, columnConfiguration] of Object.entries(
    config.columns
  )) {
    //
    // Ensure columns that are marked as not removable are not hidden
    //
    if (!columnConfiguration.removable) {
      gridState.removedColumnKeys = gridState.removedColumnKeys.filter(
        (key) => key !== columnKey
      );
    }

    //
    // Ensure columnOrder contains all columns.
    //
    const currentColIndex = newColumnOrder.indexOf(columnKey);
    if (currentColIndex < 0) {
      // This column was not in the state last time. Include it to the right of the previous column
      newColumnOrder.splice(nextColumnInsertIndex++, 0, columnKey);

      // Ensure new columns which are "default hide" are indeed hidden by default.
      if (
        defaultRemovedColumns.has(columnKey) &&
        gridState.removedColumnKeys.indexOf(columnKey) < 0
      ) {
        gridState.removedColumnKeys.push(columnKey);
      }
    } else {
      nextColumnInsertIndex = currentColIndex + 1;
    }
  }

  if (newColumnOrder.length != gridState.columnOrder.length) {
    gridState.applyColumnOrder(newColumnOrder);
  }
};

export class BmGridColumnConfiguration
  implements SortableAndSearchable, Formattable, Renderable
{
  // The title to display on the column header row
  public headingText: string;

  // Extracts the itemValue from the item.
  public extractor: (item: any) => any;
  public classExtractor: (item: any) => string = () => "";

  // Formatter. Note item will be undefined if formatting an aggregate column
  public formatter: (value: any, item: any | undefined) => any = (item) => item;

  // CSV Formatter - default to the same as the normal Formatter but with some string safety
  public csvformatter: (
    value: any,
    item: any | undefined,
    stringFormatter: (s: string) => string
  ) => string = function (
    this: BmGridColumnConfiguration,
    value: any,
    item: any,
    stringFormatter: (s: string) => string
  ) {
    const ob = this.formatter(value, item);
    if (ob instanceof String || typeof ob == "string") {
      return stringFormatter(ob as string);
    }
    return ob?.toString() ?? "";
  };

  // width?
  public shrink: boolean = false;

  // Renderable
  public cardColumnSpan: number = 1;
  public align: ColumnAlignment = "start";
  public renderer: Renderer = componentToRenderer(DataCellText);
  public rendererMetadata: RendererMetadata = {}; // supplemental data to send to the display component

  // Footer
  public footer: ColumnFooterRenderer | null = null;

  // Sort and Search
  public comparer: Comparer = autoComparer;
  public matcher: Matcher = stringMatcher;
  public columnFilter?: ColumnFilter;
  public enumType?: () => readonly BaseEnumType[];

  // capability
  public sortableAndFilterable: boolean = true;
  public groupable: boolean = true;
  public removable: boolean = true;

  // Grid Controls
  public showInGridControls: boolean = true;

  public extractAndFormat(item: any) {
    return this.formatter(this.extractor(item), item);
  }

  public constructor(
    headingText: string,
    extractor: (item: any) => any,
    options: Partial<BmGridColumnConfiguration>
  ) {
    this.headingText = headingText;
    this.extractor = extractor;
    Object.assign(this, options);
  }
}

export type SummaryExtractor = (
  item: any,
  cols: BmGridColumnConfigurations
) => any;

export enum SummaryPlacement {
  Column,
}

export class BmGridSummaryConfiguration implements Renderable {
  public extractor: SummaryExtractor;
  public groupSummaryValueExtractor: GroupSummaryValueExtractor =
    allTheSameAggregator;
  public placement: SummaryPlacement = SummaryPlacement.Column;

  // The column to place the groupSummaryValueExtractor into if using Column placement
  public columnKey?: string;

  public cardColumnSpan: number = 1;
  public align: ColumnAlignment = "start";
  public renderer: Renderer = componentToRenderer(DataCellText);
  public rendererMetadata: any = {};

  public constructor(
    extractor: SummaryExtractor,
    groupSummaryValueExtractor: GroupSummaryValueExtractor,
    options: Partial<BmGridSummaryConfiguration>
  ) {
    this.extractor = extractor;
    this.groupSummaryValueExtractor = groupSummaryValueExtractor;
    Object.assign(this, options);
  }
}

export type BmGridColumnConfigurations = {
  [columnKey: string]: BmGridColumnConfiguration;
};

export class BmGridConfiguration {
  public defaultGridState?: BmGridState;
  public columns: BmGridColumnConfigurations = {};
  public summaries: BmGridSummaryConfiguration[] = [];
  // TODO - Once itemKey is initialised in every use case make it mandatory and remove
  // itemKey as an option in BmGrid props.
  public itemKey?: (item: any) => string;
  public title?: string;
  public onRowClick?: (item: any) => void;
}

// Internal Types

type RowType = "content" | "group";
export const isContentRow = (row?: Row): row is ContentRow =>
  row?.rowType === "content";
export const isRowGroup = (row?: Row): row is RowGroup =>
  row?.rowType === "group";

export interface Row {
  // internal state
  isSelected: boolean;
  isExpanded: boolean;
  isVisible: boolean;
  rowType: RowType;
  rowKey: string;
  dropBelow: boolean;
}

export interface ContentRow<T = any> extends Row {
  rowType: "content";
  item: T;
}

export interface RowGroup extends Row {
  rowType: "group";

  // The key of the column this group is grouping on
  columnKey: string;

  // The value from the grouped column this is grouping
  columnValue: string;

  // child rows/groups
  children: Row[];

  // whether this group is a parent of other groups
  isParentOfGroups: boolean;

  // The aggregate values by column key
  aggregateValues: {
    [columnKey: string]: { value: any; summary: BmGridSummaryConfiguration };
  };
}

export type SortableColumnConfigurations = {
  [columnKey: string]: BmGridColumnConfiguration & {
    sortDescending?: boolean;
  };
};

export type RowsMapWithIndex = Map<string, [Row, number]>;

/**
 * Get the columns that are used in the groupings. The order of the keys defines the grouping level.
 */
export function getGroupedColumns(
  gridConfiguration: BmGridConfiguration,
  gridState: BmGridState
): BmGridColumnConfigurations {
  return Object.fromEntries(
    Object.entries(gridConfiguration.columns)
      .filter(([, value]) => value.groupable)
      .filter(([key]) => gridState?.groupBy.indexOf(key) !== -1)
  );
}

/**
 * The column definitions for rendering. Only includes visible (not removed) columns,
 * excluding grouped columns.
 */
export function getVisibleColumns(
  gridConfiguration: BmGridConfiguration,
  gridState: BmGridState,
  groupedColumns: BmGridColumnConfigurations = getGroupedColumns(
    gridConfiguration,
    gridState
  )
): BmGridColumnConfigurations {
  const toRet = {} as BmGridColumnConfigurations;
  const groupedKeys = Object.keys(groupedColumns);

  Object.entries(gridConfiguration.columns)
    .filter(([key]) => groupedKeys.indexOf(key) === -1)
    .filter(([key]) => !gridState.removedColumnKeys.some((g) => g === key))
    .forEach(([key, col]) => (toRet[key] = col));
  return toRet;
}

/**
 * Columns with filters applied (excluding grouped columns).
 */
export function getFilterableColumns(
  gridConfiguration: BmGridConfiguration,
  gridState: BmGridState,
  groupedColumns: BmGridColumnConfigurations = getGroupedColumns(
    gridConfiguration,
    gridState
  )
): BmGridColumnConfigurations {
  const toRet = {} as BmGridColumnConfigurations;
  const groupedKeys = Object.keys(groupedColumns);

  Object.entries(gridConfiguration.columns)
    .filter(([key]) => groupedKeys.indexOf(key) === -1)
    .filter(([key]) => gridState.columnFilters[key])
    .forEach(([key, col]) => (toRet[key] = col));
  return toRet;
}

/**
 * Columns used for sorting, with an augmented `sortDescending` flag.
 */
export function getSortableColumns(
  gridConfiguration: BmGridConfiguration,
  gridState: BmGridState
): SortableColumnConfigurations {
  return Object.fromEntries(
    gridState.sortBy
      .map((colMatcher) => {
        const value = gridConfiguration.columns[colMatcher.columnKey];
        if (value === undefined) {
          /* eslint-disable no-console */
          console.error(
            `sort defined on column ${colMatcher.columnKey}, but column does not exist`
          );
          return null;
        }
        if (!value.sortableAndFilterable) {
          return null;
        }
        return [
          colMatcher.columnKey,
          Object.assign({ sortDescending: colMatcher?.descending }, value),
        ];
      })
      .filter((x) => x != null) as any[]
  );
}

export function extractItemKey(
  item: any,
  gridConfiguration: BmGridConfiguration,
  itemKey?: (item: any) => string
): string {
  return (gridConfiguration.itemKey ?? itemKey)?.(item) ?? "";
}

/**
 * A flat view of ContentRow and RowGroup nodes.
 */
export function getFlattenedRows(rootNode: readonly Row[]): Row[] {
  const recursiveCrawler = (rows: readonly Row[]) => {
    let returnRows: Row[] = [];
    for (const row of rows) {
      returnRows.push(row);
      if (row.rowType === "group") {
        returnRows = returnRows.concat(
          recursiveCrawler((row as RowGroup).children)
        );
      }
    }

    return returnRows;
  };

  return recursiveCrawler(rootNode);
}

export function buildRowsMapWithIndex(
  virtualGrid: readonly Row[]
): RowsMapWithIndex {
  return toMap(
    getFlattenedRows(virtualGrid).map(
      (row, idx) => [row, idx] as [Row, number]
    ),
    ([row]) => row.rowKey + row.rowType
  );
}

export function countGroupedRows(
  items: readonly any[],
  gridConfiguration: BmGridConfiguration,
  groupedColumns: BmGridColumnConfigurations
): number {
  if (Object.keys(groupedColumns).length == 0) {
    return items.length;
  }

  const groupColumnKey = Object.keys(groupedColumns)[0];
  const valueExtractor = gridConfiguration.columns[groupColumnKey].extractor;
  return new Set(items.map(valueExtractor)).size;
}

/**
 * Temporary helper to detect duplicate or invalid item keys.
 * Returns a reason string when invalid, otherwise undefined.
 */
export function findInvalidItemKeyReason(
  items: readonly any[],
  extractKey: (item: any) => string
): string | undefined {
  const uniqueKeys = new Set();

  for (const item of items) {
    const key = extractKey(item);
    if (key === undefined) {
      return "undefined";
    }
    if (typeof key !== "string") {
      return "non_string";
    }
    if (uniqueKeys.has(key)) {
      return "duplicate";
    }
    uniqueKeys.add(key);
  }

  return undefined;
}

export function logInvalidItemKeys(reason: string): void {
  eventLogger.logEvent("grid_keys" + reason, {});
}

export function computeNextSelectedItemKeys(
  row: Row | undefined,
  selectedItemKeys: readonly string[],
  virtualGrid: readonly Row[],
  rowSelectable: (row: Row) => boolean
): string[] {
  const selectedItems = new Set(selectedItemKeys);

  if (row) {
    if (row.isSelected || !rowSelectable(row)) {
      selectedItems.delete(row?.rowKey);
    } else {
      selectedItems.add(row?.rowKey);
    }
  } else {
    const isAllSelected = virtualGrid.every(
      (r) => r.isSelected || !rowSelectable(r)
    );

    if (isAllSelected) {
      const visibleSelectedRows = virtualGrid.filter((r) =>
        selectedItems.has(r.rowKey)
      );

      visibleSelectedRows.forEach((r) => {
        selectedItems.delete(r.rowKey);
      });
    } else {
      const visibleSelectableRows = virtualGrid.filter((r) => rowSelectable(r));

      visibleSelectableRows.forEach((r) => {
        selectedItems.add(r.rowKey);
      });
    }
  }

  return [...selectedItems.values()];
}

export interface CalculateRowGroupsOptions {
  rows: ContentRow[];
  gridConfiguration: BmGridConfiguration;
  gridState: BmGridState;
  groupedColumns: BmGridColumnConfigurations;
  defaultExpandGroups: boolean;
  previousRowsMap: RowsMapWithIndex;
  groupIndex?: number;
  reapplyRowStates?: boolean;
  parentGroupValues?: string[];
}

/**
 * Recursive grouping. Breaks the virtual grid into the specified groups.
 */
export function calculateRowGroups(
  options: CalculateRowGroupsOptions
): RowGroup[] {
  const {
    rows,
    gridConfiguration,
    gridState,
    groupedColumns,
    defaultExpandGroups,
    previousRowsMap,
    groupIndex = 0,
    reapplyRowStates = true,
    parentGroupValues = [],
  } = options;

  const groupRows: RowGroup[] = [];
  const groupColumnKey = Object.keys(groupedColumns)[groupIndex];
  const valueExtractor = gridConfiguration.columns[groupColumnKey].extractor;

  // @TODO: [BREW-4732] Grouping on a nullish value should resolve to a placeholder

  // This is a bit of a blunt instrument as it simply toStrings the extracted value in the item to determine the
  //  groups. Ideally this should use the comparers on the columns - although performance will suffer.
  const groupByValue = groupBy(rows, (row) => valueExtractor(row.item));

  for (const entry of Object.entries(groupByValue)) {
    let childRows: Row[] | RowGroup[] = [];
    const childGroup = groupIndex === gridState.groupBy.length - 1;

    if (!childGroup) {
      childRows = calculateRowGroups({
        ...options,
        rows: entry[1],
        groupIndex: groupIndex + 1,
        parentGroupValues: [...parentGroupValues, entry[0]],
      });
    } else {
      childRows = entry[1];
    }

    const childItems = getFlattenedRows(childRows)
      .filter((r) => r.rowType === "content")
      .map((r) => (r as ContentRow).item);

    const aggregateValues: {
      [key: string]: { value: any; summary: BmGridSummaryConfiguration };
    } = {};

    for (const summary of gridConfiguration.summaries) {
      const extractedValues = childItems.map((item) =>
        summary.extractor(item, gridConfiguration.columns)
      );

      if (summary.placement === SummaryPlacement.Column) {
        if (
          !summary.columnKey ||
          !Object.prototype.hasOwnProperty.call(
            gridConfiguration.columns,
            summary.columnKey
          )
        ) {
          throw new Error(
            "Summary with column placement must specify a column key to an existing column."
          );
        }
        aggregateValues[summary.columnKey] = {
          value: summary.groupSummaryValueExtractor(
            extractedValues,
            entry[0],
            parentGroupValues
          ),
          summary: summary,
        };
      }
    }
    const groupRowKey = getGroupRowKey(
      groupColumnKey,
      entry[0],
      parentGroupValues
    );

    const existingRow = reapplyRowStates
      ? previousRowsMap.get(groupRowKey + "group")
      : undefined;

    groupRows.push({
      rowKey: groupRowKey,
      columnKey: groupColumnKey,
      columnValue: entry[0],
      get isSelected() {
        return childRows.every((r: Row | RowGroup) => r.isSelected);
      },
      set isSelected(value: boolean) {
        childRows.forEach((r: Row | RowGroup) => (r.isSelected = value));
      },
      isExpanded: existingRow ? existingRow[0].isExpanded : defaultExpandGroups,
      children: childRows,
      isParentOfGroups: !childGroup,
      rowType: "group",
      aggregateValues,
    } as RowGroup);
  }

  return groupRows;
}

export interface BuildVirtualGridOptions {
  items: readonly any[];
  gridConfiguration: BmGridConfiguration;
  gridState: BmGridState;
  itemKey?: (item: any) => string;
  allowSort: boolean;
  selectedItemKeys: readonly string[];
  expandedItemKeys: readonly string[];
  defaultExpandExpandable: boolean;
  defaultExpandGroups: boolean;
  itemNoun: string;
  reapplyRowStates?: boolean;
  previousVirtualGrid: readonly Row[];
}

export interface BuildVirtualGridResult {
  rows: readonly Row[];
  filterSummary: string;
}

/**
 * Full virtual-grid pipeline: map items → search → column filters → group → sort.
 */
export function buildVirtualGrid(
  options: BuildVirtualGridOptions
): BuildVirtualGridResult {
  const {
    items,
    gridConfiguration,
    gridState,
    itemKey,
    allowSort,
    selectedItemKeys,
    expandedItemKeys,
    defaultExpandExpandable,
    defaultExpandGroups,
    itemNoun,
    reapplyRowStates = true,
    previousVirtualGrid,
  } = options;

  const groupedColumns = getGroupedColumns(gridConfiguration, gridState);
  const visibleColumns = getVisibleColumns(
    gridConfiguration,
    gridState,
    groupedColumns
  );
  const filterableColumns = getFilterableColumns(
    gridConfiguration,
    gridState,
    groupedColumns
  );
  const sortableColumns = getSortableColumns(gridConfiguration, gridState);
  const previousRowsMap = buildRowsMapWithIndex(previousVirtualGrid);
  const selectedKeysSet = new Set(selectedItemKeys);

  let newGrid: Row[] = [];

  // We only want to map-in our original row indexes if we're sorting
  // sort & map will still take time, and are pointless if we don't care
  if (allowSort) {
    newGrid = items
      .map((i: any) => {
        const rowKey = extractItemKey(i, gridConfiguration, itemKey);
        const existingRow = reapplyRowStates
          ? previousRowsMap.get(rowKey + "content")
          : undefined;

        return [
          existingRow ? existingRow[1] : -1,
          {
            rowKey: rowKey,
            item: i,
            isExpanded: existingRow
              ? existingRow[0].isExpanded
              : defaultExpandExpandable ||
                expandedItemKeys.indexOf(rowKey) > -1,
            isSelected: selectedKeysSet.has(rowKey),
            rowType: "content",
            dropBelow: false,
          } as ContentRow,
        ];
      })
      .sort((a, b) => {
        return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
      })
      .map(([_, row]) => row as ContentRow);
  } else {
    newGrid = items.map((i: any) => {
      const rowKey = extractItemKey(i, gridConfiguration, itemKey);
      const existingRow = reapplyRowStates
        ? previousRowsMap.get(rowKey + "content")
        : undefined;

      return {
        rowKey: rowKey,
        item: i,
        isExpanded: existingRow
          ? existingRow[0].isExpanded
          : defaultExpandExpandable || expandedItemKeys.indexOf(rowKey) > -1,
        isSelected: selectedKeysSet.has(rowKey),
        rowType: "content",
        dropBelow: false,
      } as ContentRow;
    });
  }

  // grid search filter on all visible columns including those that are grouped
  if (gridState.gridSearchFilter) {
    const searchString = gridState.gridSearchFilter;
    newGrid = (newGrid as ContentRow[]).filter((row) =>
      Object.values({
        ...visibleColumns,
        ...groupedColumns,
      }).some((col) => col.matcher(col.extractor(row.item), searchString))
    );
  }

  newGrid = applyColumnFilters(
    newGrid as ContentRow[],
    {
      ...filterableColumns,
      ...groupedColumns,
    },
    gridState.columnFilters
  );

  if (Object.keys(groupedColumns).length > 0) {
    newGrid = calculateRowGroups({
      rows: newGrid as ContentRow[],
      gridConfiguration,
      gridState,
      groupedColumns,
      defaultExpandGroups,
      previousRowsMap,
      groupIndex: 0,
      reapplyRowStates,
    }) as Row[];
  }

  // Don't sort if we don't allow it; items are normally already in the desired order
  if (Object.keys(sortableColumns).length > 0 && allowSort) {
    applySorting(newGrid, sortableColumns);
  }

  const filterSummary = `${newGrid.length} / ${countGroupedRows(
    items,
    gridConfiguration,
    groupedColumns
  )} ${itemNoun}`;

  return {
    rows: Object.freeze(newGrid),
    filterSummary,
  };
}

/**
 * Validate grid state against configuration, apply defaults, and persist when changed.
 * Returns a cloned internal state when it should be updated, otherwise undefined.
 */
export function sanitizeAndSyncGridState(
  gridState: BmGridState,
  gridConfiguration: BmGridConfiguration,
  gridStateInternal: BmGridState
): BmGridState | undefined {
  const state = gridState;
  if (!state.useGridDefault) {
    const availableColumnKeys = new Set(Object.keys(gridConfiguration.columns));

    const sortAndFilterColumnKeys = new Set(
      Object.entries(gridConfiguration.columns)
        .filter((x) => x[1].sortableAndFilterable)
        .map((x) => x[0])
    );

    const newGroupBy = state.groupBy.filter((x) => availableColumnKeys.has(x));

    const newSortBy = state.sortBy.filter((x) =>
      sortAndFilterColumnKeys.has(x.columnKey)
    );

    const oldFilters = Object.entries(state.columnFilters);
    const newFilters = oldFilters.filter((x) =>
      sortAndFilterColumnKeys.has(x[0])
    );

    if (
      newGroupBy.length !== state.groupBy.length ||
      newSortBy.length !== state.sortBy.length ||
      newFilters.length !== oldFilters.length
    ) {
      state.groupBy = newGroupBy;
      state.sortBy = newSortBy;
      state.columnFilters = Object.fromEntries(newFilters);
    }
  }

  if (gridState.useGridDefault) {
    const stateId = gridState.stateId;
    // Clone deep used as object.assign uses a shallow copy.
    // This caused defaultGridState to point to the same object as gridState
    Object.assign(gridState, cloneDeep(gridConfiguration.defaultGridState));
    gridState.stateId = stateId;
    gridState.useGridDefault = false;
  }

  if (!isEqual(gridState, gridStateInternal)) {
    BmGridStateRepository.save();
    return cloneDeep(gridState);
  }

  return undefined;
}

/**
 * Filters a flat (ContentRows only) grid by the column filters.
 */
export function applyColumnFilters(
  flatGrid: ContentRow[],
  columns: BmGridColumnConfigurations,
  columnFilterState: { [columnKey: string]: any }
): ContentRow[] {
  for (const [columnKey, columnDef] of Object.entries(columns).filter(
    ([_, col]) => col.columnFilter !== undefined
  )) {
    // Apply each column filter in any order
    const filterState = columnFilterState[columnKey];

    // If there is no state for a column filter it is deemed inactive
    if (filterState === undefined) {
      continue;
    }

    const columnFilter = columnDef.columnFilter;

    if (columnFilter !== undefined) {
      const filterFunction = columnFilter.buildApply(filterState);
      flatGrid = flatGrid.filter((row) =>
        filterFunction(
          columnDef.extractor(row.item),
          columnDef.extractAndFormat(row.item)
        )
      );
    }
  }

  return flatGrid;
}

/**
 * Sorts a flat or nested grid (RowGroup and ContentRow tree).
 */
export function applySorting(
  flatOrGroupedGrid: Row[],
  sortBy: SortableColumnConfigurations
) {
  const recursiveGroupSorter = (rows: Row[], _depth: number = 0) => {
    if (!rows.length) {
      return;
    }

    if (rows[0].rowType === "content") {
      (rows as ContentRow[]).sort((a, b) => {
        for (const [, sortColumn] of Object.entries(sortBy)) {
          if (!sortColumn.sortableAndFilterable) {
            continue;
          }

          const valA = sortColumn.extractor(a.item);
          const valB = sortColumn.extractor(b.item);
          const sortVal = sortColumn.comparer(valA, valB);
          if (sortVal !== 0) {
            return sortVal * (sortColumn.sortDescending ? -1 : 1);
          }
        }

        return 0;
      });
    } else if (rows[0].rowType === "group") {
      (rows as RowGroup[]).sort((a, b) => {
        for (const [sortColumnKey, sortColumn] of Object.entries(sortBy)) {
          if (!sortColumn.sortableAndFilterable) {
            continue;
          }

          let sortVal = 0;

          if (a.columnKey === sortColumnKey) {
            sortVal = sortColumn.comparer(a.columnValue, b.columnValue);
          } else {
            if (
              Object.prototype.hasOwnProperty.call(
                a.aggregateValues,
                sortColumnKey
              )
            ) {
              sortVal = sortColumn.comparer(
                a.aggregateValues[sortColumnKey].value,
                b.aggregateValues[sortColumnKey].value
              );
            }
          }

          if (sortVal !== 0) {
            return sortVal * (sortColumn.sortDescending ? -1 : 1);
          }
        }

        return 0;
      });

      for (const row of rows as RowGroup[]) {
        recursiveGroupSorter(row.children, _depth + 1);
      }
    }
  };

  recursiveGroupSorter(flatOrGroupedGrid, 0);
}
